import { useCallback } from 'react';
import { expValEquals } from '@confluence/feature-experiments';

export type ApprovalReviewStatus = 'APPROVED' | 'CANCELED' | 'DENIED' | 'PENDING';

type UsePreventFirstPublishWithoutApprovalProps = {
	approvalRequestLoading?: boolean;
	approvalReviewStatus?: ApprovalReviewStatus | null;
	contentId: string | undefined;
	disallowFirstPublish?: boolean;
	isUnpublishedDraft: boolean;
	onPublishBlocked?: () => void;
	spaceKey: string | undefined;
	shouldPreventPublish?: boolean;
};

export const usePreventFirstPublishWithoutApproval = ({
	approvalRequestLoading = false,
	approvalReviewStatus = null,
	contentId,
	disallowFirstPublish = false,
	isUnpublishedDraft,
	onPublishBlocked,
	spaceKey,
	shouldPreventPublish = false,
}: UsePreventFirstPublishWithoutApprovalProps): (() => Promise<boolean>) =>
	useCallback(async () => {
		if (!contentId || !spaceKey) {
			return false;
		}

		if (shouldPreventPublish) {
			onPublishBlocked?.();
			return true;
		}

		if (!expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
			return false;
		}

		if (!isUnpublishedDraft) {
			return false;
		}

		if (approvalRequestLoading && disallowFirstPublish) {
			onPublishBlocked?.();
			return true;
		}

		if (!disallowFirstPublish && !shouldPreventPublish) {
			return false;
		}

		if (disallowFirstPublish && approvalReviewStatus === 'APPROVED') {
			return false;
		}

		onPublishBlocked?.();
		return true;
	}, [
		approvalRequestLoading,
		approvalReviewStatus,
		contentId,
		disallowFirstPublish,
		isUnpublishedDraft,
		onPublishBlocked,
		shouldPreventPublish,
		spaceKey,
	]);
